import React, { createContext, useContext, useState, useEffect, useRef, ReactNode, useCallback } from 'react';
import type { AuthState } from '@okta/okta-auth-js';
import { User } from '../types';
import { authClient } from '../Utils/Common';
import { consoleUsersApi } from '../services/api/console/users.api';

interface AuthContextType {
  user: User | null;
  logout: () => void;
  isLoading: boolean;
  isProfileReady: boolean; // true only after full permissions are fetched
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// include tableau_admin so it isn't stripped to ''
const normalizeRole = (role?: string): 'admin' | 'business_analyst' | 'tableau_admin' | '' => {
  const cleanRole = (role || '').toLowerCase().trim();
  if (cleanRole === 'admin' || cleanRole === 'business_analyst' || cleanRole === 'tableau_admin') {
    return cleanRole;
  }
  return '';
};

// Who Okta says is signed in, read from their ID token.
interface Identity {
  email: string;
  firstName: string;
  lastName: string;
}

const identityFrom = (authState: AuthState): Identity | null => {
  if (!authState.isAuthenticated) return null;
  const claims: any = authState.idToken?.claims ?? {};
  if (!claims.email) return null;
  return {
    email: String(claims.email).toLowerCase(),
    firstName: claims.given_name || '',
    lastName: claims.family_name || '',
  };
};

// useUserInfo and the Sidebar read the signed-in email from storage, so it is
// written and cleared here, in step with Okta, and nowhere else.
const rememberEmail = (email: string) => {
  sessionStorage.setItem('userEmail', email);
  localStorage.setItem('currentUser', email);
};

const forgetEmail = () => {
  sessionStorage.removeItem('userEmail');
  localStorage.removeItem('currentUser');
};

// Signed in, without console permissions: shown until the profile arrives,
// and kept if it can't be fetched.
const unprivilegedUser = (identity: Identity): User => ({
  userId: identity.email,
  email: identity.email,
  firstName: identity.firstName,
  lastName: identity.lastName,
  isAuthenticated: true,
  role: '',
  canAccessConsole: false,
  isAdmin: false,
});

const fetchProfile = async (identity: Identity, signal: AbortSignal): Promise<User> => {
  try {
    const me = await consoleUsersApi.getMe(identity.email, signal);
    return {
      userId: identity.email,
      email: identity.email,
      firstName: me.user_first_name || identity.firstName,
      lastName: me.user_last_name || identity.lastName,
      role: normalizeRole(me.user_role),
      canAccessConsole: !!me.can_access_console,
      isAdmin: !!me.is_admin,
      isAuthenticated: true,
    };
  } catch {
    // A failed lookup (e.g. 403) means no console access, not an error.
    return unprivilegedUser(identity);
  }
};

// Mirrors Okta's sign-in state, which <Security> (rendered inside this
// provider) starts and keeps current. The signed-in person comes only from
// Okta's ID token, never from storage, which can hold an earlier session's
// email.
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProfileReady, setIsProfileReady] = useState(false);

  // The person Okta currently reports as signed in, if any.
  const identityRef = useRef<Identity | null>(null);
  // The profile request in flight. Starting another one, a different person
  // signing in, signing out and unmounting all cancel it: the browser stops
  // the request, and a cancelled request changes nothing.
  const profileRequestRef = useRef<AbortController | null>(null);
  // Set by logout(): the session is ending. Okta keeps the tokens while it
  // revokes them, so a sign-in state reported meanwhile must not bring the
  // person back. Cleared once Okta reports nobody signed in.
  const signingOutRef = useRef(false);

  const loadProfile = useCallback(async (identity: Identity) => {
    profileRequestRef.current?.abort();
    const request = new AbortController();
    profileRequestRef.current = request;

    const profile = await fetchProfile(identity, request.signal);
    if (request.signal.aborted) return;

    profileRequestRef.current = null;
    setUser(profile);
    setIsProfileReady(true);
  }, []);

  const endSession = useCallback(() => {
    profileRequestRef.current?.abort();
    profileRequestRef.current = null;
    identityRef.current = null;
    forgetEmail();
    setUser(null);
    setIsProfileReady(false);
  }, []);

  useEffect(() => {
    const handleAuthState = (authState: AuthState | null) => {
      if (!authState) return; // Okta hasn't worked out the sign-in state yet
      setIsLoading(false);

      const identity = identityFrom(authState);
      if (!identity) {
        signingOutRef.current = false;
        endSession();
        return;
      }
      if (signingOutRef.current) return;
      // Same person (e.g. a renewed token): their profile is already loaded.
      if (identity.email === identityRef.current?.email) return;

      identityRef.current = identity;
      rememberEmail(identity.email);
      setUser(unprivilegedUser(identity));
      setIsProfileReady(false);
      void loadProfile(identity);
    };

    handleAuthState(authClient.authStateManager.getAuthState());
    authClient.authStateManager.subscribe(handleAuthState);

    return () => {
      authClient.authStateManager.unsubscribe(handleAuthState);
      profileRequestRef.current?.abort();
      profileRequestRef.current = null;
      // Forgotten so a remount (e.g. React StrictMode in development) reads
      // the sign-in state afresh and loads the profile again.
      identityRef.current = null;
    };
  }, [endSession, loadProfile]);

  // The profile is fetched when someone signs in (including after a page
  // reload) and whenever refreshProfile() is called; there is no polling, so
  // console access granted meanwhile shows up after a reload.
  const refreshProfile = useCallback(async () => {
    const identity = identityRef.current;
    if (!identity) return;
    await loadProfile(identity);
  }, [loadProfile]);

  const logout = useCallback(() => {
    signingOutRef.current = true;
    endSession();
  }, [endSession]);

  const value: AuthContextType = {
    user,
    logout,
    isLoading,
    isProfileReady,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};